'use client';
import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PostFiltersComp from './postFilters/postFilters.index';
import styles from './posts.module.scss';
import { __PostsFiltersDataType, __PostsFiltersType, __PostsListSessionType, __PostsPropsType } from './posts.type';
import { RestAPI, useBP, useRealTimeState, useThen } from 'scripts';
import { __LoadedPage, __PageQueryKey } from 'scripts/paginationQuery';
import { Button, Drawer, Dropdown, Empty, message, Spin } from 'antd';
import { PostCardComp } from 'components';
import {
    __FetchPosts,
    __GetParentCategories,
    __POSTS_LIST_SESSION_KEY,
    __POSTS_PAGE_SIZE,
    __PostsFilterQueryKey,
    __PostsPageURL,
    __PostsQueryKey,
    __PostsSessionMatchesUrl,
} from './posts.script';
import { GlobalContext } from 'context';
import { API } from 'data';
import { CategoryFieldType, CategoryType, PaginationType, PostType } from 'types';
import { useSearchParams } from 'next/navigation';
import { InView } from 'react-intersection-observer';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function PostsComp(props: __PostsPropsType) {
    const { locations, categories } = useContext(GlobalContext);
    const queryClient = useQueryClient();
    const locationKey = useMemo(() => (locations?.map((i) => i.id) ?? []).join(','), [locations]);
    const propsFilterKey = useMemo(() => __PostsFilterQueryKey(props.initialFilter), [props.initialFilter]);
    const initialSessionRef = useRef<__PostsListSessionType | undefined | null>(null);
    if (initialSessionRef.current === null) {
        const session = queryClient.getQueryData<__PostsListSessionType>(__POSTS_LIST_SESSION_KEY);
        const hasCache = session && queryClient.getQueryData(__PostsQueryKey(session.queryBaseKey));
        const filterKeyMatches =
            session?.queryBaseKey === propsFilterKey ||
            (hasCache && session && __PostsSessionMatchesUrl(session.filters));
        if (session && filterKeyMatches && hasCache) {
            initialSessionRef.current = session;
        } else {
            initialSessionRef.current = undefined;
        }
    }
    const initialSession = initialSessionRef.current;
    const hasStoredPostsOnInit = !!queryClient.getQueryData(__PostsQueryKey(propsFilterKey));

    const [filters, setFiltersState] = useState<__PostsFiltersType>(() => {
        if (initialSession) {
            const page =
                queryClient.getQueryData<number>(__PageQueryKey(__PostsQueryKey(initialSession.queryBaseKey))) ??
                __LoadedPage(
                    queryClient.getQueryData(__PostsQueryKey(initialSession.queryBaseKey)),
                    __POSTS_PAGE_SIZE,
                ) ??
                initialSession.filters.page ??
                1;
            return { ...initialSession.filters, page };
        }
        if (hasStoredPostsOnInit) {
            const page =
                queryClient.getQueryData<number>(__PageQueryKey(__PostsQueryKey(propsFilterKey))) ??
                __LoadedPage(queryClient.getQueryData(__PostsQueryKey(propsFilterKey)), __POSTS_PAGE_SIZE) ??
                props.initialFilter.page ??
                1;
            return { ...props.initialFilter, page };
        }
        return { ...props.initialFilter, page: 1 };
    });
    const [filterData, setFilterDataState] = useState<__PostsFiltersDataType | undefined>(
        () => initialSession?.filterData ?? props.initialFilterData,
    );
    const [filterModal, setFilterModal] = useState(false);
    const restoredFromSessionRef = useRef(!!initialSession);

    // page is excluded from query base
    const filterQueryKey = useMemo(() => __PostsFilterQueryKey(filters), [filters]);
    const queryKey = useMemo(() => __PostsQueryKey(filterQueryKey), [filterQueryKey]);
    const pageQueryKey = useMemo(() => __PageQueryKey(queryKey), [queryKey]);

    const prevFilterQueryKeyRef = useRef(filterQueryKey);
    if (prevFilterQueryKeyRef.current !== filterQueryKey) {
        prevFilterQueryKeyRef.current = filterQueryKey;
        const hasStored = !!queryClient.getQueryData<PaginationType<PostType<'list'>>>(queryKey);
        const nextPage = hasStored ? (queryClient.getQueryData<number>(pageQueryKey) ?? 1) : 1;
        if ((filters.page ?? 1) !== nextPage) setFiltersState((prev) => ({ ...prev, page: nextPage }));
    }

    const persistSession = useCallback(
        (nextFilters: __PostsFiltersType, nextFilterData?: __PostsFiltersDataType) => {
            const key = __PostsFilterQueryKey(nextFilters);
            queryClient.setQueryData(__POSTS_LIST_SESSION_KEY, {
                queryBaseKey: key,
                filters: nextFilters,
                filterData: nextFilterData ?? filterData,
            } satisfies __PostsListSessionType);
            queryClient.setQueryData(__PageQueryKey(__PostsQueryKey(key)), nextFilters.page ?? 1);
        },
        [queryClient, filterData],
    );

    const setFilters = useCallback(
        (value: React.SetStateAction<__PostsFiltersType>) => {
            setFiltersState((prev) => {
                const next = typeof value === 'function' ? value(prev) : { ...prev, ...value };
                persistSession(next);
                return next;
            });
        },
        [persistSession],
    );

    const setFilterData = useCallback(
        (value: React.SetStateAction<__PostsFiltersDataType | undefined>) => {
            setFilterDataState((prev) => {
                const next = typeof value === 'function' ? value(prev) : value;
                persistSession(filters, next);
                return next;
            });
        },
        [filters, persistSession],
    );

    const hasCachedPosts = !!queryClient.getQueryData<PaginationType<PostType<'list'>>>(queryKey);
    const useServerInitialData = !hasCachedPosts && (props.initialFilter.page ?? 1) === 1 && (filters.page ?? 1) === 1;
    const query = useQuery({
        queryKey,
        queryFn: () => {
            const prev =
                (filters.page ?? 1) > 1
                    ? queryClient.getQueryData<PaginationType<PostType<'list'>>>(queryKey)
                    : undefined;
            return __FetchPosts(filters, locations, prev);
        },
        initialData: hasCachedPosts ? undefined : useServerInitialData ? props.initialData : undefined,
        staleTime: 30 * 60 * 1000,
    });
    const bp = useBP();
    const isDesktop = bp?.LG_GTE;
    const searchParams = useSearchParams();
    const loadMore = useRealTimeState(false);

    const posts = query.data;

    // Sync URL on mount: restore full query from session, or drop ?page when there is no cached list data
    useLayoutEffect(() => {
        if (restoredFromSessionRef.current) {
            const listUrl = __PostsPageURL(filters);
            window.history.replaceState(null, '', listUrl.pathname + listUrl.search);
            restoredFromSessionRef.current = false;
            return;
        }
        if (!hasCachedPosts) {
            const atPageOne = { ...filters, page: 1 };
            const listUrl = __PostsPageURL(atPageOne);
            window.history.replaceState(null, '', listUrl.pathname + listUrl.search);
            queryClient.setQueryData(pageQueryKey, 1);
        }
    }, []);

    const refetchPosts = useCallback(() => {
        queryClient
            .cancelQueries({ queryKey })
            .then(() => {
                if ((filters.page ?? 1) === 1) {
                    queryClient.setQueryData(queryKey, undefined);
                    queryClient.setQueryData(pageQueryKey, 1);
                } else loadMore.set(true);
                return query.refetch();
            })
            .catch(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                message.error('خطایی در بارگذاری آگهی های بیشتر رخ داده است. لطفا مجددا تلاش نمایید');
            })
            .finally(() => loadMore.set(false));
    }, [queryClient, queryKey, pageQueryKey, filters.page, query, loadMore]);

    useThen(() => {
        const listUrl = __PostsPageURL(filters);
        window.history.replaceState(null, '', listUrl.pathname + listUrl.search);
        persistSession(filters, filterData);
        refetchPosts();
    }, [filterQueryKey, filters.page]);

    // Locations hydrate after mount; refetch only — do not rewrite URL (avoids ?page-only flash on back navigation)
    useThen(() => {
        if (!locationKey) return;
        refetchPosts();
    }, [locationKey]);

    useThen(() => {
        if (filterData && filters.category?.slug !== filterData.category?.slug) {
            if (!filters.category) setFilterData({ subcategories: __GetParentCategories(categories) });
            else {
                setFilterData(undefined);
                Promise.all([
                    RestAPI.get<CategoryType<'detail'>>(API.category.detail(filters.category.slug)),
                    RestAPI.get<PaginationType<CategoryType<'list'>>>(API.category.list, {
                        parent: filters.category.id,
                        page_size: 100,
                        ordering: '-order',
                    }),
                    RestAPI.get<PaginationType<CategoryFieldType<'detail'>>>(API.categoryField.list, {
                        is_filter: true,
                        page_size: 100,
                        category_ancestors: filters.category.id,
                    }),
                ]).then((res) =>
                    setFilterData({
                        category: res[0].data,
                        subcategories: res[1].data.data,
                        variantFields: res[2].data.data,
                    }),
                );
            }
        }
    }, [filters.category]);

    useThen(() => {
        const search = searchParams.get('search');
        if (search?.length && filters.search !== search) setFilters({ ...filters, page: 1, search });
        else if ((!search || search.length === 0) && filters.search)
            setFilters({ ...filters, page: 1, search: undefined });
    }, [searchParams]);

    console.log(posts);

    return (
        <div className={`${styles['post-list-layout']} d-flex`}>
            {!bp || isDesktop ? (
                <div className={`${styles['filter-layout']}`}>
                    <PostFiltersComp filters={filters} setFilters={setFilters} filterData={filterData} />
                </div>
            ) : (
                <Drawer open={filterModal} onClose={() => setFilterModal(false)} width={'100%'}>
                    <PostFiltersComp
                        filters={filters}
                        setFilters={setFilters}
                        filterData={filterData}
                        onClose={() => setFilterModal(false)}
                    />
                </Drawer>
            )}
            <div className={`${styles['post-layout']} flex-column`}>
                <div className="d-md-none g-1 mv-2">
                    <Button className="flex-1" onClick={() => setFilterModal(true)}>
                        فیلترها
                    </Button>
                    <Dropdown menu={{ items: [{ label: 'جدیدترین', key: '-renewed_at' }] }} trigger={['click']}>
                        <Button className="flex-1" type="primary">
                            مرتب سازی
                        </Button>
                    </Dropdown>
                </div>
                {posts ? (
                    posts?.data.length ? (
                        <>
                            <div className={`${styles['grid']}`}>
                                {posts.data.map((post, index) => (
                                    <React.Fragment key={index}>
                                        {posts.num_of_pages !== filters.page && index >= posts.data.length - 12 ? (
                                            <InView
                                                as="div"
                                                onChange={(inView) => {
                                                    if (inView && !loadMore.get())
                                                        setFilters({ ...filters, page: (filters.page ?? 1) + 1 });
                                                }}
                                            >
                                                <PostCardComp data={post} />
                                            </InView>
                                        ) : (
                                            <PostCardComp data={post} />
                                        )}
                                    </React.Fragment>
                                ))}
                                <div />
                            </div>
                            {posts.num_of_pages !== filters.page ? (
                                <InView
                                    as="div"
                                    className="center-content pv-2 w-100"
                                    onChange={(inView) => {
                                        if (inView && !loadMore.get())
                                            setFilters({ ...filters, page: (filters.page ?? 1) + 1 });
                                    }}
                                >
                                    <Spin />
                                </InView>
                            ) : null}
                        </>
                    ) : (
                        <Empty
                            className="pv-6 flex-1 center-content flex-column"
                            description={
                                'آگهی ای یافت نشد. ' + locations
                                    ? 'برای جستجوی گسترده‌تر فیلترهای مورد نظر خود را تغییر دهید.'
                                    : ''
                            }
                        />
                    )
                ) : (
                    <div className={`w-100 pv-9 center-content flex-1`}>
                        <Spin />
                    </div>
                )}
            </div>
        </div>
    );
}

export default PostsComp;
